/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * Onboarding wizard + first-time-use hooks.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { authFetch, getJson, postJson } from './_http';

/* ─── Onboarding + FTU — Wave 5a.1 (FULLY IMPLEMENTED) ─────────────────── */

/**
 * Wizard state stored in `users.onboarding_progress`.
 *
 * Mirrors `packages/db/src/schema/auth.ts#OnboardingProgress`. We re-declare
 * here rather than importing because `@barel/api-client` is consumed by
 * the extension which doesn't depend on `@barel/db`.
 */
export interface OnboardingProgress {
  step: number;
  goal?: 'inbox' | 'campaigns' | 'both';
  inboxMode?: 'auto' | 'review';
  connectMethod?: 'extension' | 'cookie';
  connected?: boolean;
  plan?: 'discover' | 'scale' | 'agency';
  updatedAt?: number;
}

export interface OnboardingStateResponse {
  progress: OnboardingProgress | null;
  completedAt: number | null;
  goal: 'inbox' | 'campaigns' | 'both' | null;
  defaultInboxMode: 'auto' | 'review' | null;
}

export interface OnboardingCompleteResponse {
  completedAt: number;
  goal: 'inbox' | 'campaigns' | 'both';
  defaultInboxMode: 'auto' | 'review' | null;
}

/** `GET /api/v1/me/onboarding` — fetch wizard state + completion status. */
export function useOnboardingState(
  options: { enabled?: boolean } = {},
): UseQueryResult<OnboardingStateResponse, ApiError> {
  const client = useApiClient();
  return useQuery<OnboardingStateResponse, ApiError>({
    queryKey: ['onboarding-state'],
    queryFn: () => getJson<OnboardingStateResponse>(client, '/api/v1/me/onboarding'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `PATCH /api/v1/me/onboarding` — partial-update wizard state. */
export function useUpdateOnboarding(): UseMutationResult<
  { progress: OnboardingProgress },
  ApiError,
  Partial<OnboardingProgress>
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ progress: OnboardingProgress }, ApiError, Partial<OnboardingProgress>>({
    mutationFn: (vars) =>
      authFetch<{ progress: OnboardingProgress }>(client, '/api/v1/me/onboarding', {
        method: 'PATCH',
        body: JSON.stringify(vars),
      }),
    onSuccess: (data) => {
      qc.setQueryData<OnboardingStateResponse>(['onboarding-state'], (prev) => ({
        progress: data.progress,
        completedAt: prev?.completedAt ?? null,
        goal: prev?.goal ?? null,
        defaultInboxMode: prev?.defaultInboxMode ?? null,
      }));
    },
  });
}

/** `POST /api/v1/me/onboarding/complete` — finalise onboarding. */
export function useCompleteOnboarding(): UseMutationResult<
  OnboardingCompleteResponse,
  ApiError,
  Partial<OnboardingProgress> | void
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<OnboardingCompleteResponse, ApiError, Partial<OnboardingProgress> | void>({
    mutationFn: (vars) =>
      postJson<OnboardingCompleteResponse>(client, '/api/v1/me/onboarding/complete', vars ?? {}),
    onSuccess: (data) => {
      qc.setQueryData<OnboardingStateResponse>(['onboarding-state'], () => ({
        progress: null,
        completedAt: data.completedAt,
        goal: data.goal,
        defaultInboxMode: data.defaultInboxMode,
      }));
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** `POST /api/v1/me/onboarding/restart` — re-open the wizard from Settings. */
export function useRestartOnboarding(): UseMutationResult<{ ok: true }, ApiError, void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () => postJson<{ ok: true }>(client, '/api/v1/me/onboarding/restart', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding-state'] });
    },
  });
}

export interface OnboardingAiDemoProgress {
  percent: number;
  threadsScanned: number;
  messagesScanned: number;
  totalEstimated: number;
  complete: boolean;
  updatedAt: number | null;
}

export interface OnboardingAiDemoAccount {
  id: string;
  name: string;
  initials: string;
  avatarUrl: string | null;
  linkedinUrn: string | null;
  profileUrl: string | null;
  status: 'active' | 'paused' | 'error' | 'limited';
  statusInfo: string | null;
  historyConvs: number;
  historyEligible: boolean;
}

export interface OnboardingAiDemoMessage {
  id: string;
  role: 'in' | 'out' | 'system';
  text: string;
  at: number | null;
  highlight?: boolean;
}

export interface OnboardingAiDemoConversation {
  id: string;
  contactName: string;
  contactInitials: string;
  contactRole: string | null;
  contactCompany: string | null;
  contactAvatarUrl: string | null;
  contactLinkedinUrn: string | null;
  messages: OnboardingAiDemoMessage[];
}

export interface OnboardingAiDemoSuggestion {
  body: string;
  source: 'cached' | 'llm' | 'fallback';
  draftId: string | null;
}

export interface OnboardingAiDemoInsight {
  personalized: boolean;
  confidence: number;
  selectionReason: string;
  businessSummary: string | null;
  audience: string | null;
  offer: string | null;
  styleSummary: string;
  styleTraits: string[];
  styleExamplesUsed: number;
  hotSignalCriteria: string[];
  isHot: boolean;
  hotScore: number;
  hotReason: string;
  hotSignals: string[];
}

export interface OnboardingAiDemoSignal {
  label: string;
  value: string;
}

export interface OnboardingAiDemoResponse {
  status: 'backfilling' | 'ready' | 'empty' | 'failed';
  reason: string | null;
  progress: OnboardingAiDemoProgress;
  account: OnboardingAiDemoAccount | null;
  conversation: OnboardingAiDemoConversation | null;
  suggestion: OnboardingAiDemoSuggestion | null;
  insight: OnboardingAiDemoInsight | null;
  signals: OnboardingAiDemoSignal[];
}

export interface OnboardingAiDemoRetryResponse {
  ok: boolean;
  status: 'backfilling' | 'failed';
  reason: string | null;
  progress: OnboardingAiDemoProgress;
}

/** `GET /api/v1/onboarding/ai-demo` — real Step 4 backfill/demo status. */
export function useOnboardingAiDemo(
  options: {
    enabled?: boolean;
    refetchInterval?: number | false;
  } = {},
): UseQueryResult<OnboardingAiDemoResponse, ApiError> {
  const client = useApiClient();
  return useQuery<OnboardingAiDemoResponse, ApiError>({
    queryKey: ['onboarding-ai-demo'],
    queryFn: () => getJson<OnboardingAiDemoResponse>(client, '/api/v1/onboarding/ai-demo'),
    enabled: options.enabled,
    // Step 4 needs polling only while the extension is still backfilling.
    // Once the personalized result arrives, stop calling the two-model path.
    refetchInterval: (query) =>
      query.state.data?.status === 'backfilling' ? options.refetchInterval : false,
    retry: false,
  });
}

/** `POST /api/v1/onboarding/ai-demo/retry` — force a fresh Step 4 backfill nudge. */
export function useRetryOnboardingAiDemo(): UseMutationResult<
  OnboardingAiDemoRetryResponse,
  ApiError,
  void
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<OnboardingAiDemoRetryResponse, ApiError, void>({
    mutationFn: () =>
      postJson<OnboardingAiDemoRetryResponse>(client, '/api/v1/onboarding/ai-demo/retry', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding-ai-demo'] });
    },
  });
}

/** `PATCH /api/v1/me/visited-pages` — merge-add visited route prefixes. */
export function useTrackVisitedPages(): UseMutationResult<
  { visitedPages: string[] },
  ApiError,
  { pages: string[] }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ visitedPages: string[] }, ApiError, { pages: string[] }>({
    mutationFn: (vars) =>
      authFetch<{ visitedPages: string[] }>(client, '/api/v1/me/visited-pages', {
        method: 'PATCH',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** `POST /api/v1/me/dev/seed-demo` — load demo dataset (US-025, US-036). */
export function useSeedDemo(): UseMutationResult<
  { seeded: true; idempotent: boolean },
  ApiError,
  void
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ seeded: true; idempotent: boolean }, ApiError, void>({
    mutationFn: () =>
      postJson<{ seeded: true; idempotent: boolean }>(client, '/api/v1/me/dev/seed-demo', {}),
    onSuccess: () => {
      // Invalidate everything that the demo dataset touches — Wave 5a.2 will
      // expand this list as the relevant query keys land.
      qc.clear();
    },
  });
}
